import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BookmarkPlus, Check, ChevronDown, ChevronUp, Loader2, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/api";

/**
 * Saved views bar — pill list of the user's saved filter views for a given
 * scope. Lets the user save current filters under a name, click a view to
 * re-apply it, rename a view, or delete it.
 *
 * Props:
 *  - scope: "candidats" | "pepites" | "alertes" | "vivier"
 *  - currentParams: object representing the active query params (sent on save)
 *  - onApply(params): callback invoked when the user clicks a saved view
 *  - activeMatch(params): optional fn(view.params) → bool to highlight the
 *    currently-applied view. If omitted, no view is marked active.
 */
export default function SavedViewsBar({ scope, currentParams, onApply, activeMatch }) {
    const [views, setViews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [creating, setCreating] = useState(false);
    const [newName, setNewName] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [editingName, setEditingName] = useState("");
    const newInputRef = useRef(null);
    const editInputRef = useRef(null);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/saved-views", { params: { scope } });
            setViews(data);
        } catch (_e) {
            setViews([]);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scope]);

    useEffect(() => {
        if (creating && newInputRef.current) newInputRef.current.focus();
    }, [creating]);

    useEffect(() => {
        if (editingId && editInputRef.current) {
            editInputRef.current.focus();
            editInputRef.current.select();
        }
    }, [editingId]);

    async function handleCreate() {
        const name = newName.trim();
        if (!name) {
            setCreating(false);
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/saved-views", { name, scope, params: currentParams || {} });
            setViews((prev) => [data, ...prev]);
            toast.success(`Vue « ${data.name} » enregistrée ✅`);
            setNewName("");
            setCreating(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Impossible de sauvegarder la vue");
        } finally {
            setBusy(false);
        }
    }

    async function handleRename(view) {
        const name = editingName.trim();
        if (!name || name === view.name) {
            setEditingId(null);
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.patch(`/saved-views/${view.id}`, { name });
            setViews((prev) => prev.map((v) => (v.id === view.id ? data : v)));
            toast.success("Vue renommée ✅");
            setEditingId(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Renommage impossible");
        } finally {
            setBusy(false);
        }
    }

    async function handleDelete(view) {
        if (!window.confirm(`Supprimer la vue « ${view.name} » ?`)) return;
        setBusy(true);
        try {
            await api.delete(`/saved-views/${view.id}`);
            setViews((prev) => prev.filter((v) => v.id !== view.id));
            toast.success("Vue supprimée");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Suppression impossible");
        } finally {
            setBusy(false);
        }
    }

    async function handleUpdateCurrent(view) {
        setBusy(true);
        try {
            const { data } = await api.patch(`/saved-views/${view.id}`, { params: currentParams || {} });
            setViews((prev) => prev.map((v) => (v.id === view.id ? data : v)));
            toast.success(`Vue « ${data.name} » mise à jour ✅`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Mise à jour impossible");
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="flex flex-wrap items-center gap-2" data-testid="saved-views-bar">
            <BookmarkPlus className="h-4 w-4 text-slate-400 shrink-0" />
            <span className="text-xs font-bold uppercase tracking-widest text-slate-400 mr-1">
                Mes vues :
            </span>
            {loading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />
            ) : views.length === 0 && !creating ? (
                <span className="text-[11px] italic text-slate-400">
                    Aucune vue sauvegardée pour l'instant.
                </span>
            ) : null}
            <AnimatePresence initial={false}>
                {views.map((v) => {
                    const isActive = activeMatch ? activeMatch(v.params) : false;
                    const isEditing = editingId === v.id;
                    if (isEditing) {
                        return (
                            <motion.form
                                key={v.id}
                                initial={{ opacity: 0, y: -4 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -4 }}
                                onSubmit={(e) => { e.preventDefault(); handleRename(v); }}
                                className="inline-flex items-center gap-1 h-8 pl-3 pr-1 rounded-full bg-white border-2 border-shariti-orange text-xs font-bold text-shariti-black"
                            >
                                <input
                                    ref={editInputRef}
                                    value={editingName}
                                    onChange={(e) => setEditingName(e.target.value)}
                                    onBlur={() => handleRename(v)}
                                    onKeyDown={(e) => { if (e.key === "Escape") setEditingId(null); }}
                                    data-testid={`saved-view-rename-input-${v.id}`}
                                    maxLength={60}
                                    className="bg-transparent outline-none w-32 text-xs font-bold text-shariti-black"
                                />
                                <button
                                    type="submit"
                                    disabled={busy}
                                    data-testid={`saved-view-rename-confirm-${v.id}`}
                                    className="h-6 w-6 inline-flex items-center justify-center rounded-full text-emerald-600 hover:bg-emerald-50 transition"
                                >
                                    <Check className="h-3.5 w-3.5" />
                                </button>
                            </motion.form>
                        );
                    }
                    return (
                        <motion.div
                            key={v.id}
                            initial={{ opacity: 0, y: -4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -4 }}
                            className={`inline-flex items-center gap-0.5 h-8 pl-3 pr-1 rounded-full border text-xs font-bold transition ${
                                isActive
                                    ? "bg-shariti-orange text-white border-shariti-orange shadow-btn"
                                    : "bg-white text-slate-700 border-shariti-gray hover:border-shariti-orange hover:text-shariti-orange"
                            }`}
                            data-testid={`saved-view-${v.id}`}
                        >
                            <button
                                type="button"
                                onClick={() => onApply?.(v.params)}
                                title={`Appliquer la vue « ${v.name} »`}
                                data-testid={`saved-view-apply-${v.id}`}
                                className="pr-1 max-w-[180px] truncate"
                            >
                                {v.name}
                            </button>
                            <button
                                type="button"
                                onClick={() => handleUpdateCurrent(v)}
                                title="Mettre à jour avec les filtres actuels"
                                data-testid={`saved-view-update-${v.id}`}
                                className={`h-6 w-6 inline-flex items-center justify-center rounded-full transition ${
                                    isActive
                                        ? "hover:bg-white/20"
                                        : "text-slate-400 hover:text-emerald-600 hover:bg-emerald-50"
                                }`}
                            >
                                <BookmarkPlus className="h-3 w-3" />
                            </button>
                            <button
                                type="button"
                                onClick={() => { setEditingId(v.id); setEditingName(v.name); }}
                                title="Renommer"
                                data-testid={`saved-view-rename-${v.id}`}
                                className={`h-6 w-6 inline-flex items-center justify-center rounded-full transition ${
                                    isActive
                                        ? "hover:bg-white/20"
                                        : "text-slate-400 hover:text-shariti-orange hover:bg-shariti-orange-soft"
                                }`}
                            >
                                <Pencil className="h-3 w-3" />
                            </button>
                            <button
                                type="button"
                                onClick={() => handleDelete(v)}
                                title="Supprimer"
                                data-testid={`saved-view-delete-${v.id}`}
                                className={`h-6 w-6 inline-flex items-center justify-center rounded-full transition ${
                                    isActive
                                        ? "hover:bg-white/20"
                                        : "text-slate-400 hover:text-shariti-red hover:bg-red-50"
                                }`}
                            >
                                <Trash2 className="h-3 w-3" />
                            </button>
                        </motion.div>
                    );
                })}
            </AnimatePresence>
            {creating ? (
                <form
                    onSubmit={(e) => { e.preventDefault(); handleCreate(); }}
                    className="inline-flex items-center gap-1 h-8 pl-3 pr-1 rounded-full bg-white border-2 border-shariti-orange text-xs font-bold text-shariti-black"
                >
                    <input
                        ref={newInputRef}
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onBlur={() => { if (!newName.trim()) setCreating(false); }}
                        onKeyDown={(e) => { if (e.key === "Escape") { setCreating(false); setNewName(""); } }}
                        placeholder="Nommer cette vue…"
                        maxLength={60}
                        data-testid="saved-view-new-input"
                        className="bg-transparent outline-none w-40 text-xs font-bold text-shariti-black placeholder:text-slate-400 placeholder:font-medium"
                    />
                    <button
                        type="submit"
                        disabled={busy || !newName.trim()}
                        data-testid="saved-view-new-confirm"
                        className="h-6 w-6 inline-flex items-center justify-center rounded-full text-emerald-600 hover:bg-emerald-50 transition disabled:opacity-40"
                    >
                        <Check className="h-3.5 w-3.5" />
                    </button>
                    <button
                        type="button"
                        onClick={() => { setCreating(false); setNewName(""); }}
                        data-testid="saved-view-new-cancel"
                        className="h-6 w-6 inline-flex items-center justify-center rounded-full text-slate-400 hover:text-shariti-red hover:bg-red-50 transition"
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                </form>
            ) : (
                <button
                    type="button"
                    onClick={() => setCreating(true)}
                    data-testid="saved-view-new"
                    className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-shariti-orange-soft text-shariti-orange border border-shariti-orange/30 hover:bg-shariti-orange hover:text-white transition text-xs font-extrabold"
                >
                    <BookmarkPlus className="h-3.5 w-3.5" />
                    Sauvegarder la vue
                </button>
            )}
        </div>
    );
}

/**
 * Collapsible wrapper for the entire filter section. Persists open/closed
 * state in localStorage so each user keeps their preference between visits.
 */
export function CollapsibleFilters({ storageKey, summary, children, defaultOpen = true }) {
    const [open, setOpen] = useState(() => {
        if (typeof window === "undefined") return defaultOpen;
        const stored = window.localStorage.getItem(`prerec.filters.${storageKey}`);
        if (stored === "1") return true;
        if (stored === "0") return false;
        return defaultOpen;
    });

    function toggle() {
        const next = !open;
        setOpen(next);
        try {
            window.localStorage.setItem(`prerec.filters.${storageKey}`, next ? "1" : "0");
        } catch (_e) {
            /* localStorage may be disabled */
        }
    }

    return (
        <div className="card-soft p-3 md:p-4" data-testid={`filters-${storageKey}`}>
            <button
                type="button"
                onClick={toggle}
                data-testid={`filters-toggle-${storageKey}`}
                aria-expanded={open}
                className="w-full flex items-center gap-2 text-left"
            >
                <span className="text-[11px] font-extrabold uppercase tracking-widest text-shariti-orange">
                    Filtres
                </span>
                {!open && summary ? (
                    <span className="text-xs font-medium text-slate-500 truncate">{summary}</span>
                ) : null}
                <span className="ml-auto inline-flex items-center justify-center h-7 w-7 rounded-full bg-shariti-cream text-slate-500">
                    {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </span>
            </button>
            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className="overflow-hidden"
                    >
                        <div className="pt-3 space-y-3">
                            {children}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
